(() => {
  if (customElements.get('nobl-video-gallery')) return;
  let hintSeen = false;
  class NoblVideoGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.cards = [...this.querySelectorAll('[data-video-card]')];
      this.track = this.querySelector('[data-video-track]');
      this.dialog = this.querySelector('dialog');
      this.panel = this.querySelector('[data-video-panel]');
      this.video = this.querySelector('video');
      this.status = this.querySelector('[data-video-status]');
      this.hint = this.querySelector('[data-video-hint]');
      this.toggle = this.querySelector('[data-video-action="toggle"]');
      this.mute = this.querySelector('[data-video-action="mute"]');
      this.index = 0;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.controls = this.querySelector('.nobl-video__controls');
      this.prevButton = this.querySelector('[data-video-action="scroll-previous"]');
      this.nextButton = this.querySelector('[data-video-action="scroll-next"]');
      this.warmed = new Map();
      this.track.addEventListener('scroll', () => {
        if (this.arrowFrame) return;
        this.arrowFrame = requestAnimationFrame(() => { this.arrowFrame = null; this.updateArrows(); });
      }, {passive:true});
      if ('ResizeObserver' in window) new ResizeObserver(() => this.updateArrows()).observe(this.track);
      this.updateArrows();
      this.lazyPosters();
      ['pointerenter','focusin','touchstart'].forEach(name => this.track.addEventListener(name, event => {
        const card = event.target.closest?.('[data-video-card]');
        if (card) this.warm(this.cards.indexOf(card));
      }, {passive:true, capture:name === 'pointerenter'}));
      this.addEventListener('click', event => {
        const card = event.target.closest('[data-video-card]');
        if (card) { this.opener = card; this.open(this.cards.indexOf(card)); return; }
        if (this.suppressClick && !event.target.closest('[data-video-action]')) { this.suppressClick = false; return; }
        const action = event.target.closest('[data-video-action]')?.dataset.videoAction;
        if (action === 'close') this.dialog.close();
        if (action === 'next') this.navigate(1);
        if (action === 'previous') this.navigate(-1);
        if (action === 'toggle' || event.target === this.video || event.target === this.panel) {
          if (this.video.paused) this.resume(); else this.video.pause();
        }
        if (action === 'mute') { this.video.muted = !this.video.muted; this.syncControls(); }
        if (action === 'scroll-next' || action === 'scroll-previous') {
          this.slide(action === 'scroll-next' ? 1 : -1);
        }
      });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
      this.dialog.addEventListener('close', () => {
        this.playRequest = (this.playRequest || 0) + 1;
        this.dismissHint(); this.cancelGesture(); this.animation?.cancel();
        this.video.pause(); this.video.removeAttribute('src'); this.video.load(); this.coolDown([]);
        this.status.textContent = '';
        document.documentElement.style.overflow = this.previousOverflow;
        this.opener?.focus({preventScroll:true});
      });
      this.dialog.addEventListener('keydown', event => {
        const directions = {ArrowDown:1,ArrowRight:1,ArrowUp:-1,ArrowLeft:-1};
        if (directions[event.key]) { event.preventDefault(); this.navigate(directions[event.key]); }
      });
      this.panel.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || event.target.closest('button')) return;
        this.suppressClick = false;
        this.gesture = {id:event.pointerId,x:event.clientX,y:event.clientY};
        this.panel.setPointerCapture(event.pointerId);
      });
      this.panel.addEventListener('pointermove', event => {
        if (this.gesture?.id !== event.pointerId) return;
        const dy = event.clientY-this.gesture.y;
        if (!this.reduced.matches) this.video.style.transform = `translateY(${Math.max(-100,Math.min(100,dy*.35))}px)`;
      });
      this.panel.addEventListener('pointerup', event => {
        if (this.gesture?.id !== event.pointerId) return;
        const dy = event.clientY-this.gesture.y, dx = event.clientX-this.gesture.x;
        this.cancelGesture();
        if (Math.abs(dy) >= 60 && Math.abs(dy) > Math.abs(dx)*1.2) {
          this.suppressClick = true; this.navigate(dy < 0 ? 1 : -1);
        }
      });
      this.panel.addEventListener('pointercancel', () => this.cancelGesture());
      this.panel.addEventListener('lostpointercapture', () => this.cancelGesture());
      this.panel.addEventListener('wheel', event => {
        if (!this.dialog.open || event.ctrlKey || Math.abs(event.deltaX)>Math.abs(event.deltaY)) return;
        event.preventDefault();
        const now = Date.now();
        if (now < (this.wheelUntil || 0)) return;
        if (now-(this.lastWheel || 0)>180) this.wheelDelta = 0;
        this.lastWheel = now;
        this.wheelDelta = (this.wheelDelta || 0)+event.deltaY*(event.deltaMode===1?16:event.deltaMode===2?this.panel.clientHeight:1);
        if (Math.abs(this.wheelDelta)>=60) { this.navigate(this.wheelDelta>0?1:-1); this.wheelDelta=0; this.wheelUntil=now+700; }
      }, {passive:false});
      this.video.addEventListener('error', () => { if(this.dialog.open) this.status.textContent=this.dataset.error; });
      this.video.addEventListener('playing', () => { this.status.textContent=''; this.syncControls(); });
      this.video.addEventListener('ended', () => { if (this.video.ended) this.navigate(1); });
      ['pause','ended','volumechange'].forEach(name=>this.video.addEventListener(name,()=>this.syncControls()));
    }
    slide(direction) {
      const track = this.track, cards = this.cards;
      if (cards.length < 2) return;
      const step = cards[1].offsetLeft - cards[0].offsetLeft;
      if (step <= 0) return;
      const max = track.scrollWidth - track.clientWidth;
      const gap = step - cards[0].offsetWidth;
      const perView = Math.max(1, Math.floor((track.clientWidth + gap) / step + .05));
      const current = Math.round((this.scrollTarget ?? track.scrollLeft) / step);
      const target = Math.max(0, Math.min(max, (current + direction * perView) * step));
      const first = Math.round(target / step);
      for (let i = first; i < first + perView + 1; i++) this.loadPoster?.(cards[i]);
      this.animateScroll(target);
    }
    animateScroll(target) {
      const track = this.track;
      cancelAnimationFrame(this.scrollFrame);
      const start = track.scrollLeft, distance = target - start;
      if (Math.abs(distance) < 1) { this.scrollTarget = null; return; }
      if (this.reduced.matches) { track.scrollLeft = target; this.scrollTarget = null; this.updateArrows(); return; }
      this.scrollTarget = target;
      const duration = Math.min(650, Math.max(380, Math.abs(distance) * .55));
      const begin = performance.now();
      const ease = p => p < .5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      track.classList.add('is-animating');
      const frame = now => {
        const progress = Math.min(1, (now - begin) / duration);
        track.scrollLeft = start + distance * ease(progress);
        if (progress < 1) { this.scrollFrame = requestAnimationFrame(frame); return; }
        track.classList.remove('is-animating'); this.scrollTarget = null; this.updateArrows();
      };
      this.scrollFrame = requestAnimationFrame(frame);
    }
    updateArrows() {
      if (!this.prevButton || !this.nextButton) return;
      const track = this.track, max = track.scrollWidth - track.clientWidth;
      const position = this.scrollTarget ?? track.scrollLeft;
      if (this.controls) this.controls.hidden = max <= 1;
      this.prevButton.disabled = position <= 1;
      this.nextButton.disabled = position >= max - 1;
    }
    lazyPosters() {
      const pending = this.cards.filter(card => card.querySelector('img[data-poster]'));
      this.loadPoster = card => {
        const img = card?.querySelector('img[data-poster]');
        if (!img) return;
        img.src = img.dataset.poster; img.removeAttribute('data-poster');
        this.posterObserver?.unobserve(card);
      };
      if (!pending.length) return;
      if (!('IntersectionObserver' in window)) { pending.forEach(this.loadPoster); return; }
      this.posterObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const index = this.cards.indexOf(entry.target);
        for (let i = index; i <= index + 3; i++) this.loadPoster(this.cards[i]);
      }), {rootMargin:'200px 0px'});
      pending.forEach(card => this.posterObserver.observe(card));
      const prefetch = () => {
        const visible = this.cards.findIndex(card => card.offsetLeft - this.cards[0].offsetLeft + card.offsetWidth > this.track.scrollLeft + this.track.clientWidth);
        if (visible > -1) for (let i = visible; i < visible + 3; i++) this.loadPoster(this.cards[i]);
      };
      [this.nextButton, this.track].forEach(el => el && ['pointerenter','focusin','touchstart'].forEach(name => el.addEventListener(name, prefetch, {passive:true, once:false})));
    }
    warm(index) {
      const count = this.cards.length;
      if (!count || index < 0) return;
      const src = this.cards[((index % count) + count) % count].dataset.src;
      if (!src || this.warmed.has(src) || src === this.video.getAttribute('src')) return;
      const buffer = document.createElement('video');
      buffer.muted = true; buffer.playsInline = true; buffer.preload = 'auto';
      buffer.src = src; buffer.load();
      this.warmed.set(src, buffer);
    }
    coolDown(keep) {
      this.warmed.forEach((buffer, src) => {
        if (keep.includes(src)) return;
        buffer.removeAttribute('src'); buffer.load(); this.warmed.delete(src);
      });
    }
    cancelGesture() { this.gesture=null; this.video.style.transform=''; }
    syncControls() {
      this.toggle.setAttribute('aria-label',this.video.paused?this.dataset.playLabel:this.dataset.pauseLabel);
      this.toggle.dataset.paused=String(this.video.paused);
      this.mute.setAttribute('aria-label',this.video.muted?this.dataset.unmuteLabel:this.dataset.muteLabel);
      this.mute.dataset.muted=String(this.video.muted);
    }
    dismissHint() { clearTimeout(this.hintTimer); this.hint.hidden=true; }
    open(index) {
      this.previousOverflow=document.documentElement.style.overflow;
      this.dialog.showModal(); document.documentElement.style.overflow='hidden';
      this.suppressClick=false; this.wheelDelta=0; this.wheelUntil=0; this.play(index);
      let seen=hintSeen;
      try { seen=seen || sessionStorage.getItem('nobl-video-swipe-hint')==='1'; } catch {}
      if (!seen && this.cards.length>1) {
        this.hint.hidden=false; hintSeen=true;
        try { sessionStorage.setItem('nobl-video-swipe-hint','1'); } catch {}
        this.hintTimer=setTimeout(()=>this.dismissHint(),4200);
      }
    }
    navigate(direction) {
      if (!this.dialog.open || this.cards.length<2) return;
      this.dismissHint(); this.play(this.index+direction);
      this.animation?.cancel();
      if (!this.reduced.matches) this.animation=this.video.animate([{transform:`translateY(${direction*75}%)`,opacity:.4},{transform:'translateY(0)',opacity:1}],{duration:200,easing:'ease-out'});
    }
    resume() {
      const request=this.playRequest;
      this.video.play().catch(()=>{if(request===this.playRequest && this.dialog.open) {this.status.textContent=this.dataset.playPrompt;this.syncControls();}});
    }
    play(index) {
      this.playRequest=(this.playRequest || 0)+1;
      this.index=(index+this.cards.length)%this.cards.length;
      const card=this.cards[this.index];
      this.video.pause(); this.status.textContent='';
      this.loadPoster?.(card); this.video.poster=card.querySelector('img').src; this.video.src=card.dataset.src;
      this.video.setAttribute('aria-label',card.getAttribute('aria-label'));
      this.resume(); this.syncControls();
      const count = this.cards.length, around = [this.index + 1, this.index - 1].map(i => this.cards[(i + count) % count].dataset.src);
      this.coolDown(around);
      if (count > 1) { this.warm(this.index + 1); this.warm(this.index - 1); }
    }
    disconnectedCallback() { clearTimeout(this.hintTimer); if(this.dialog?.open) this.dialog.close(); }
  }
  customElements.define('nobl-video-gallery',NoblVideoGallery);
})();
